import { ref, onMounted, onUnmounted, watch } from "vue"
import { useRestaurantStore } from "@/stores/restaurant"

export const usePageVisibility = () => {
  const isVisible = ref(!document.hidden)
  const isHidden = ref(document.hidden)

  const updateVisibility = () => {
    const wasHidden = isHidden.value
    isVisible.value = !document.hidden
    isHidden.value = document.hidden

    // eslint-disable-next-line no-console
    console.log("👁️ [PageVisibility] Status changed:", {
      isVisible: isVisible.value,
      isHidden: isHidden.value,
      wasHidden,
      timestamp: new Date().toISOString(),
    })

    // If page became visible and was previously hidden, trigger refresh
    if (isVisible.value && wasHidden) {
      // eslint-disable-next-line no-console
      console.log("👁️ [PageVisibility] Page became visible, refreshing orders...")
      refreshOrders()
    }
  }

  const refreshOrders = async () => {
    const restaurantStore = useRestaurantStore()
    if (restaurantStore.restaurant?.id) {
      // Skip refresh for SIMPLE package (no orders functionality)
      const packageType = restaurantStore.restaurant.packageType
      if (packageType === "SIMPLE") {
        // eslint-disable-next-line no-console
        console.log("⏭️ [PageVisibility] Skipping orders refresh for SIMPLE package")
        return
      }
      
      try {
        // eslint-disable-next-line no-console
        console.log("🔄 [PageVisibility] Refreshing orders after page visibility change")
        await Promise.all([
          restaurantStore.fetchActiveOrders(restaurantStore.restaurant.id),
          restaurantStore.fetchOrders(restaurantStore.restaurant.id, 1, 10),
        ])
        // eslint-disable-next-line no-console
        console.log("✅ [PageVisibility] Orders refreshed successfully")
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("❌ [PageVisibility] Error refreshing orders:", error)
      }
    }
  }

  const onVisible = (callback: () => void) => {
    watch(
      isVisible,
      (newValue, oldValue) => {
        if (newValue && !oldValue) {
          callback()
        }
      }
    )
  }

  onMounted(() => {
    // Set initial visibility
    updateVisibility()

    // Listen to visibility change events
    document.addEventListener("visibilitychange", updateVisibility)

    // eslint-disable-next-line no-console
    console.log("👁️ [PageVisibility] Page visibility listener initialized")
  })

  onUnmounted(() => {
    document.removeEventListener("visibilitychange", updateVisibility)
    // eslint-disable-next-line no-console
    console.log("👁️ [PageVisibility] Page visibility listener removed")
  })

  return {
    isVisible,
    isHidden,
    onVisible,
    refreshOrders,
  }
}
